
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TokensDeleteResult struct
 */
class TokensDeleteResult {
  /**
   * Copy struct (shallow copy)
   * @this {!TokensDeleteResult}
   * @param {!TokensDeleteResult} other Other struct
   * @returns {!TokensDeleteResult} This struct
   */
  copy (other) {
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TokensDeleteResult}
   * @returns {!TokensDeleteResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TokensDeleteResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TokensDeleteResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TokensDeleteResult}
   * @param {!TokensDeleteResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TokensDeleteResult)) {
      throw new TypeError('Instance of TokensDeleteResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TokensDeleteResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TokensDeleteResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TokensDeleteResult} other Object value
   * @returns {!TokensDeleteResult} Created struct
   */
  static fromObject (other) {
    return new TokensDeleteResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TokensDeleteResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TokensDeleteResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TokensDeleteResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 14122
  }
}

export { TokensDeleteResult };

/**
 * Fast Binary Encoding TokensDeleteResult field model
 */
class FieldModelTokensDeleteResult extends fbe.FieldModel {
  /**
   * Get the field size
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTokensDeleteResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 14122
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTokensDeleteResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTokensDeleteResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTokensDeleteResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTokensDeleteResult}
   * @param {!TokensDeleteResult} fbeValue Default value, defaults is new TokensDeleteResult()
   * @returns {!TokensDeleteResult} TokensDeleteResult value
   */
  get (fbeValue = new TokensDeleteResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTokensDeleteResult}
   * @param {!TokensDeleteResult} fbeValue TokensDeleteResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTokensDeleteResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTokensDeleteResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTokensDeleteResult}
   * @param {!TokensDeleteResult} fbeValue TokensDeleteResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTokensDeleteResult}
   * @param {TokensDeleteResult} fbeValue TokensDeleteResult value
   */
  setFields (fbeValue) {
  }
}

export { FieldModelTokensDeleteResult };

/**
 * Fast Binary Encoding TokensDeleteResult model
 */
class TokensDeleteResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTokensDeleteResult(this.buffer, 4)
  }

  /**
   * Get the TokensDeleteResult model
   * @this {!TokensDeleteResultModel}
   * @returns {!FieldModelTokensDeleteResult} model TokensDeleteResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TokensDeleteResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TokensDeleteResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TokensDeleteResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TokensDeleteResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTokensDeleteResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TokensDeleteResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TokensDeleteResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TokensDeleteResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TokensDeleteResultModel}
   * @param {!TokensDeleteResult} value TokensDeleteResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TokensDeleteResultModel}
   * @param {!TokensDeleteResult} value TokensDeleteResult value, defaults is new TokensDeleteResult()
   * @return {!object} Deserialized TokensDeleteResult value and its size
   */
  deserialize (value = new TokensDeleteResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TokensDeleteResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TokensDeleteResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TokensDeleteResultModel}
   * @param {!number} prev Previous TokensDeleteResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TokensDeleteResultModel };
